import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import AppButton from "../button/AppButton";
import AppTextInput from "../inputs/AppTextInput";
import { CreateUnitRequest } from "../../models/RequestModels/CreateUnitRequest";
import { PropertyResponse } from "../../models/ResponseModels/PropertyResponse";
import { UnitResponse } from "../../models/ResponseModels/UnitResponse";
import { useTranslation } from "../../translations/i18n";

interface UnitFormModalProps {
  open: boolean;
  loading: boolean;
  properties: PropertyResponse[];
  propertyId?: number | null;
  unit?: UnitResponse | null;
  onClose: () => void;
  onSubmit: (body: CreateUnitRequest) => void;
}

const UnitFormModal = ({
  open,
  loading,
  properties,
  propertyId,
  unit,
  onClose,
  onSubmit,
}: UnitFormModalProps) => {
  const { t } = useTranslation();

  const [selectedPropertyId, setSelectedPropertyId] = useState<number>(
    propertyId || 0,
  );
  const [name, setName] = useState("");
  const [type, setType] = useState<CreateUnitRequest["type"]>("apartment");
  const [capacity, setCapacity] = useState("2");
  const [defaultPricePerNight, setDefaultPricePerNight] = useState("");
  const [airbnbIcalUrl, setAirbnbIcalUrl] = useState("");
  const [bookingIcalUrl, setBookingIcalUrl] = useState("");
  const [exportCopied, setExportCopied] = useState(false);

  useEffect(() => {
    if (!open) {
      return;
    }

    setSelectedPropertyId(
      unit?.property_id || propertyId || properties[0]?.id || 0,
    );
    setName(unit?.name || "");
    setType(unit?.type || "apartment");
    setCapacity(unit?.capacity ? String(unit.capacity) : "2");
    setDefaultPricePerNight(
      unit?.default_price_per_night
        ? String(Number(unit.default_price_per_night))
        : "",
    );
    setAirbnbIcalUrl(unit?.airbnb_ical_url || "");
    setBookingIcalUrl(unit?.booking_ical_url || "");
    setExportCopied(false);
  }, [open, propertyId, properties, unit]);

  const resetForm = () => {
    setName("");
    setType("apartment");
    setCapacity("2");
    setDefaultPricePerNight("");
    setAirbnbIcalUrl("");
    setBookingIcalUrl("");
    setExportCopied(false);
  };

  const handleClose = () => {
    if (loading) {
      return;
    }

    resetForm();
    onClose();
  };

  const optionalUrl = (value: string): string | null => {
    const trimmedValue = value.trim();

    return trimmedValue.length > 0 ? trimmedValue : null;
  };

  const handleSubmit = () => {
    if (!selectedPropertyId || !name.trim()) {
      return;
    }

    onSubmit({
      property_id: selectedPropertyId,
      name: name.trim(),
      type,
      capacity: Number(capacity || 1),
      default_price_per_night: defaultPricePerNight
        ? Number(defaultPricePerNight)
        : null,
      active: unit?.active ?? true,
      default_cleaner_id: unit?.default_cleaner_id || null,
      airbnb_ical_url: optionalUrl(airbnbIcalUrl),
      booking_ical_url: optionalUrl(bookingIcalUrl),
    });
  };

  const handleCopyExportLink = async () => {
    if (!unit?.calendar_export_url) {
      return;
    }

    await navigator.clipboard.writeText(unit.calendar_export_url);
    setExportCopied(true);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 900 }}>
        {unit ? t("properties.editUnit") : t("properties.newUnit")}
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <FormControl fullWidth>
            <InputLabel>{t("properties.title")}</InputLabel>
            <Select
              label={t("properties.title")}
              value={selectedPropertyId || ""}
              disabled={loading || Boolean(propertyId)}
              onChange={(event) =>
                setSelectedPropertyId(Number(event.target.value))
              }
            >
              {properties.map((property) => (
                <MenuItem key={property.id} value={property.id}>
                  {property.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <AppTextInput
            label={t("properties.unitName")}
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder={t("properties.unitNamePlaceholder")}
            disabled={loading}
            required
            autoFocus
          />

          <FormControl fullWidth>
            <InputLabel>{t("properties.type")}</InputLabel>
            <Select
              label={t("properties.type")}
              value={type}
              disabled={loading}
              onChange={(event) =>
                setType(event.target.value as CreateUnitRequest["type"])
              }
            >
              <MenuItem value="apartment">{t("properties.apartment")}</MenuItem>
              <MenuItem value="room">{t("properties.room")}</MenuItem>
              <MenuItem value="studio">{t("properties.studio")}</MenuItem>
              <MenuItem value="house">{t("properties.house")}</MenuItem>
              <MenuItem value="other">{t("properties.other")}</MenuItem>
            </Select>
          </FormControl>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <AppTextInput
              label={t("properties.capacity")}
              value={capacity}
              type="number"
              onChange={(event) => setCapacity(event.target.value)}
              disabled={loading}
            />

            <AppTextInput
              label={t("properties.defaultPricePerNight")}
              value={defaultPricePerNight}
              type="number"
              onChange={(event) => setDefaultPricePerNight(event.target.value)}
              disabled={loading}
            />
          </Stack>

          <Box
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              p: 2,
              bgcolor: "background.default",
            }}
          >
            <Typography sx={{ fontWeight: 900, mb: 0.5 }}>
              {t("properties.calendarSync")}
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              {t("properties.calendarSyncHelp")}
            </Typography>

            <Stack spacing={1.5}>
              <AppTextInput
                label={t("properties.airbnbIcalLink")}
                value={airbnbIcalUrl}
                onChange={(event) => setAirbnbIcalUrl(event.target.value)}
                placeholder="https://www.airbnb.com/calendar/ical/..."
                disabled={loading}
              />

              <AppTextInput
                label={t("properties.bookingIcalLink")}
                value={bookingIcalUrl}
                onChange={(event) => setBookingIcalUrl(event.target.value)}
                placeholder="https://admin.booking.com/..."
                disabled={loading}
              />

              <Box
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  p: 1.5,
                  bgcolor: "background.paper",
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 900 }}>
                  {t("properties.rentPilotExportLink")}
                </Typography>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.5 }}
                >
                  {t("properties.rentPilotExportHelp")}
                </Typography>

                {unit?.calendar_export_url ? (
                  <Button
                    variant="outlined"
                    size="small"
                    sx={{ mt: 1 }}
                    onClick={handleCopyExportLink}
                    disabled={loading}
                  >
                    {exportCopied
                      ? t("common.copied")
                      : t("properties.copyShareExportLink")}
                  </Button>
                ) : (
                  <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                    {t("properties.exportLinkMissing")}
                  </Typography>
                )}
              </Box>
            </Stack>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <AppButton
          title={t("common.cancel")}
          variant="outlined"
          color="inherit"
          fullWidth={false}
          disabled={loading}
          onClick={handleClose}
        />

        <AppButton
          title={unit ? t("properties.saveUnit") : t("properties.createUnit")}
          fullWidth={false}
          loading={loading}
          disabled={!selectedPropertyId || !name.trim()}
          onClick={handleSubmit}
        />
      </DialogActions>
    </Dialog>
  );
};

export default UnitFormModal;
